import { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    IconButton,
    Tooltip,
    Paper,
    Collapse,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import SectionHeading from './SectionHeading';

const NAVY = '#052F5D';
const TEMP_UNIT_KEY = 'dwms.tempUnit'; // localStorage key — per-clinician preference

// Temperature is always stored canonical in Celsius on the encounter so
// older records and the PDF report don't need to know about the toggle.
// Conversion happens only at the display / input boundary.
function cToF(c) {
    if (c === '' || c == null) return '';
    const n = Number(c);
    if (!Number.isFinite(n)) return '';
    return (n * 9 / 5 + 32).toFixed(1);
}
function fToC(f) {
    if (f === '' || f == null) return '';
    const n = Number(f);
    if (!Number.isFinite(n)) return '';
    return ((n - 32) * 5 / 9).toFixed(1);
}

// BMI is derived from height + weight (kg / m²); shown only when both exist.
function bmiFor(r) {
    const w = Number(r?.weightKg), h = Number(r?.heightCm);
    if (!w || !h) return '';
    const m = h / 100;
    return m ? (w / (m * m)).toFixed(1) : '';
}

// Grouped layout: vital signs that move together stay together, and
// anthropometrics + pain sit in a separate row so a single reading is
// readable instead of nine fields fighting for horizontal space.
const VITAL_FIELDS = [
    { key: 'bp',       label: 'BP',    type: 'text',   placeholder: '120/80', xs: 6, sm: 3, md: 2.4 },
    { key: 'hr',       label: 'HR',    type: 'number', unit: 'bpm',           xs: 6, sm: 3, md: 2.4 },
    { key: 'rr',       label: 'RR',    type: 'number', unit: '/min',          xs: 6, sm: 3, md: 2.4 },
    { key: 'temp',     label: 'Temp',  type: 'number', unit: '°C',  step: '0.1', xs: 6, sm: 3, md: 2.4 },
    { key: 'spo2',     label: 'SpO₂',  type: 'number', unit: '%',             xs: 6, sm: 3, md: 2.4 },
    { key: 'rbs',      label: 'RBS',   type: 'number', unit: 'mg/dL', step: '1', xs: 6, sm: 3, md: 2.4 },
];
const BODY_FIELDS = [
    { key: 'weightKg', label: 'Weight', type: 'number', unit: 'kg',   step: '0.1', xs: 6, sm: 4, md: 4 },
    { key: 'heightCm', label: 'Height', type: 'number', unit: 'cm',                xs: 6, sm: 4, md: 4 },
    // "0–10" moved from helperText into the label suffix so the Pain
    // field stays the same height as its siblings (helperText would push
    // it taller and break row alignment).
    { key: 'painScore',label: 'Pain',   type: 'number', unit: '0–10',              xs: 6, sm: 4, md: 4 },
];

function toLocalInput(d) {
    if (!d) return '';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function emptyRow() {
    return {
        takenAt: new Date().toISOString(),
        bp: '', hr: '', rr: '', temp: '', spo2: '', rbs: '',
        weightKg: '', heightCm: '', painScore: '', notes: '',
    };
}

export default function VitalsTab({ encounter, onPatch, disabled }) {
    const rows = encounter?.vitals || [];
    // Latest reading stays expanded; older ones collapse to a one-line
    // summary so a long visit doesn't bury the form in nine-field rows.
    const [openIdx, setOpenIdx] = useState(rows.length ? rows.length - 1 : null);
    const [tempUnit, setTempUnit] = useState(() => {
        try {
            const v = localStorage.getItem(TEMP_UNIT_KEY);
            return v === 'F' ? 'F' : 'C';
        } catch { return 'C'; }
    });
    const handleTempUnit = (_, next) => {
        if (!next) return;
        setTempUnit(next);
        try { localStorage.setItem(TEMP_UNIT_KEY, next); } catch { /* ignore */ }
    };
    const tempSymbol = tempUnit === 'F' ? '°F' : '°C';
    // Stored value (Celsius) → string shown in the chosen unit.
    const displayTemp = (storedC) => {
        if (storedC === '' || storedC == null) return '';
        if (tempUnit === 'F') return cToF(storedC);
        // Trim trailing zeros for Celsius display — server stores as-typed.
        return String(storedC);
    };

    const addRow = () => {
        onPatch({ vitals: [...rows, emptyRow()] });
        setOpenIdx(rows.length); // expand the new row
    };
    const updateRow = (idx, patch) => {
        const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
        onPatch({ vitals: next });
    };
    const removeRow = (idx) => {
        onPatch({ vitals: rows.filter((_, i) => i !== idx) });
        if (openIdx === idx) setOpenIdx(null);
    };

    const latest = useMemo(() => rows[rows.length - 1] || {}, [rows]);

    const renderField = (r, idx, c) => {
        const isTemp = c.key === 'temp';
        const unit = isTemp ? tempSymbol : c.unit;
        const value = isTemp ? displayTemp(r.temp) : (r[c.key] ?? '');
        return (
            <Grid item xs={c.xs} sm={c.sm} md={c.md} key={c.key}>
                <TextField
                    label={`${c.label}${unit ? ` (${unit})` : ''}`}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    type={c.type}
                    inputProps={{ step: c.step }}
                    placeholder={c.placeholder}
                    helperText={c.hint}
                    value={value}
                    onChange={(e) => {
                        if (isTemp) {
                            // Always store Celsius. Empty input clears.
                            const raw = e.target.value;
                            if (raw === '') {
                                updateRow(idx, { temp: '' });
                            } else if (tempUnit === 'F') {
                                updateRow(idx, { temp: fToC(raw) });
                            } else {
                                updateRow(idx, { temp: raw });
                            }
                        } else {
                            updateRow(idx, { [c.key]: e.target.value });
                        }
                    }}
                />
            </Grid>
        );
    };

    const summaryLine = (r) => {
        const parts = [];
        if (r.bp) parts.push(`BP ${r.bp}`);
        if (r.hr) parts.push(`HR ${r.hr}`);
        if (r.temp !== '' && r.temp != null) parts.push(`${displayTemp(r.temp)}${tempSymbol}`);
        if (r.spo2) parts.push(`SpO₂ ${r.spo2}%`);
        if (r.rbs) parts.push(`RBS ${r.rbs}`);
        const bmi = bmiFor(r);
        if (bmi) parts.push(`BMI ${bmi}`);
        if (r.painScore) parts.push(`Pain ${r.painScore}/10`);
        return parts.length ? parts.join(' · ') : 'No values yet';
    };

    const formatTakenAt = (d) => {
        if (!d) return '—';
        const date = new Date(d);
        if (Number.isNaN(date.getTime())) return '—';
        return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    return (
        <Box>
            <SectionHeading
                title="Vitals"
                hint="Add one row per measurement. The most recent values are summarised below."
            />

            {/* Temperature unit toggle — preference persists per-clinician in
                localStorage. Switching only changes display/input; the stored
                value on the encounter stays in Celsius. */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <ToggleButtonGroup
                    value={tempUnit}
                    exclusive
                    onChange={handleTempUnit}
                    size="small"
                    aria-label="Temperature unit"
                >
                    <ToggleButton value="C" sx={{ textTransform: 'none', fontWeight: 600, px: 1.5 }}>°C</ToggleButton>
                    <ToggleButton value="F" sx={{ textTransform: 'none', fontWeight: 600, px: 1.5 }}>°F</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Latest snapshot — at-a-glance for clinicians */}
            {rows.length > 0 && (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1.5,
                        mb: 2,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(5,47,93,0.03)',
                        borderColor: 'rgba(5,47,93,0.10)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: { xs: 2, sm: 3 },
                    }}
                >
                    {[
                        ['BP', latest.bp, ''],
                        ['HR', latest.hr, 'bpm'],
                        ['RR', latest.rr, '/min'],
                        ['Temp', latest.temp !== '' && latest.temp != null ? displayTemp(latest.temp) : '', tempSymbol],
                        ['SpO₂', latest.spo2, '%'],
                        ['RBS', latest.rbs, 'mg/dL'],
                        ['BMI', bmiFor(latest), ''],
                        ['Pain', latest.painScore, '/10'],
                    ].map(([label, val, unit]) => (
                        <Box key={label} sx={{ minWidth: 64 }}>
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
                            >
                                {label}
                            </Typography>
                            <Typography sx={{ fontWeight: 600, color: NAVY, fontVariantNumeric: 'tabular-nums' }}>
                                {val !== '' && val != null ? val : '—'}
                                {val !== '' && val != null && unit ? (
                                    <Box component="span" sx={{ ml: 0.5, fontSize: 11, fontWeight: 400, color: 'text.secondary' }}>
                                        {unit}
                                    </Box>
                                ) : null}
                            </Typography>
                        </Box>
                    ))}
                </Paper>
            )}

            {/* Readings — grouped, collapsible. Older readings show as a
                one-line summary; tap to expand and edit. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {rows.length === 0 && (
                    <Box
                        sx={{
                            py: 3,
                            px: 2,
                            borderRadius: 1.5,
                            bgcolor: 'rgba(5,47,93,0.025)',
                            border: '1px dashed rgba(5,47,93,0.15)',
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            No readings yet. Click <strong>Add reading</strong> to record one.
                        </Typography>
                    </Box>
                )}
                {rows.map((r, idx) => {
                    const isOpen = openIdx === idx;
                    return (
                        <Paper
                            key={idx}
                            variant="outlined"
                            sx={{
                                borderRadius: 1.5,
                                borderColor: 'rgba(5,47,93,0.10)',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Row header — always visible */}
                            <Box
                                onClick={() => setOpenIdx(isOpen ? null : idx)}
                                sx={{
                                    px: { xs: 1.25, sm: 1.5 },
                                    py: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: 'pointer',
                                    bgcolor: isOpen ? 'rgba(5,47,93,0.03)' : 'transparent',
                                    transition: 'background-color 140ms ease',
                                    '&:hover': { bgcolor: 'rgba(5,47,93,0.04)' },
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 14, lineHeight: 1.2 }}>
                                        Reading · {formatTakenAt(r.takenAt)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                        {summaryLine(r)}
                                    </Typography>
                                </Box>
                                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                    {isOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                                </IconButton>
                                {!disabled && (
                                    <Tooltip title="Remove reading" arrow>
                                        <IconButton
                                            onClick={(e) => { e.stopPropagation(); removeRow(idx); }}
                                            size="small"
                                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                                        >
                                            <DeleteOutlineRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Collapse in={isOpen} unmountOnExit>
                                <Box sx={{ p: { xs: 1.5, sm: 2 }, pt: 1.25, borderTop: '1px solid rgba(5,47,93,0.06)' }}>
                                    <Box sx={{ mb: 1.5 }}>
                                        <TextField
                                            label="Taken at"
                                            size="small"
                                            type="datetime-local"
                                            disabled={disabled}
                                            InputLabelProps={{ shrink: true }}
                                            value={toLocalInput(r.takenAt)}
                                            onChange={(e) => updateRow(idx, {
                                                takenAt: e.target.value ? new Date(e.target.value).toISOString() : '',
                                            })}
                                            sx={{ width: { xs: '100%', sm: 220 } }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, display: 'block', mb: 0.75 }}
                                    >
                                        Vital signs
                                    </Typography>
                                    <Grid container spacing={1} sx={{ mb: 1.5 }}>
                                        {VITAL_FIELDS.map((c) => renderField(r, idx, c))}
                                    </Grid>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, display: 'block', mb: 0.75 }}
                                    >
                                        Body & pain
                                    </Typography>
                                    <Grid container spacing={1} sx={{ mb: 1.5 }}>
                                        {BODY_FIELDS.map((c) => renderField(r, idx, c))}
                                    </Grid>
                                    <TextField
                                        label="Notes"
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={1}
                                        disabled={disabled}
                                        value={r.notes || ''}
                                        onChange={(e) => updateRow(idx, { notes: e.target.value })}
                                    />
                                </Box>
                            </Collapse>
                        </Paper>
                    );
                })}
            </Box>

            {!disabled && (
                <Button
                    onClick={addRow}
                    startIcon={<AddRoundedIcon />}
                    variant="outlined"
                    sx={{
                        mt: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: 'rgba(5,47,93,0.25)',
                        color: NAVY,
                        '&:hover': { borderColor: NAVY, bgcolor: 'rgba(5,47,93,0.04)' },
                    }}
                >
                    Add reading
                </Button>
            )}
        </Box>
    );
}
